import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import Switch from '@mui/material/Switch';

import AttachmentsContainer from '../../../../components/AttachmentsContainer';
import useToast from '../../../../hooks/useToast';
import useAttachmentInputHandlers from '../../../../../hooks/useAttachmentInputHandlers';
import commonTexts from '../texts.json';
import screenTexts from './texts.json';
import {
  TouchpointContainer,
  Row,
  StyledTextField,
  StyledFormControlLabel,
  MediaHint,
  SectionDescription,
  OfferTitle,
} from './styles';
import OfferForm from './OfferForm';

const texts = { ...commonTexts, ...screenTexts };

// Matches the upsellMedia storage rule; mobile pre-caches a video this large before showing it.
const MAX_FILE_UPLOAD_SIZE_MB = 50;
const MAX_FILE_UPLOAD_SIZE = MAX_FILE_UPLOAD_SIZE_MB * 1000000;

const getFileNameFromPath = (path) => path.split('/').pop() || path;

/**
 * One touchpoint of an upsell strategy: enabled switch, message text, a
 * single photo/video attachment, and the offer mini form. With `isMessageOptional` (the pop-up A/B test's group B) an
 * empty message and media stand for the control touchpoint's.
 */
const TouchpointEditor = ({
  title,
  touchpoint,
  onChange,
  products,
  currency,
  programs,
  mealPlans,
  isMessageOptional,
}) => {
  const { showToast } = useToast();
  const { onFilesSelected } = useAttachmentInputHandlers({
    showToast,
    fileSizeErrorText: texts.fileSizeError,
    maxFileSizeBytes: MAX_FILE_UPLOAD_SIZE,
    maxFileSizeMb: MAX_FILE_UPLOAD_SIZE_MB,
  });

  const { message } = touchpoint;

  const fileAttachments = useMemo(() => {
    if (touchpoint.pendingAttachment) {
      return [touchpoint.pendingAttachment];
    }
    if (message.mediaPath) {
      return [{
        name: getFileNameFromPath(message.mediaPath),
        url: message.mediaPath,
        isUploaded: true,
      }];
    }
    return [];
  }, [touchpoint.pendingAttachment, message.mediaPath]);

  const handleFilesSelected = useCallback((event) => {
    onFilesSelected(event, (updater) => {
      const selected = typeof updater === 'function' ? updater([]) : updater;
      if (!selected.length) {
        return;
      }
      const file = selected[selected.length - 1];
      if (!file.fileType.startsWith('image/') && !file.fileType.startsWith('video/')) {
        showToast(texts.invalidMediaType, { error: true });
        return;
      }
      // A touchpoint carries a single photo or video — a new selection replaces it.
      onChange({ pendingAttachment: file });
    });
  }, [onFilesSelected, onChange, showToast]);

  const handleFileRemoved = useCallback(() => {
    onChange({
      pendingAttachment: null,
      message: {
        ...message,
        mediaType: null,
        mediaUrl: '',
        mediaPath: '',
      },
    });
  }, [onChange, message]);

  return (
    <TouchpointContainer>
      <Row>
        <StyledFormControlLabel
          control={(
            <Switch
              checked={!!touchpoint.enabled}
              onChange={(event) => onChange({ enabled: event.target.checked })}
              color="primary"
            />
          )}
          label={title}
        />
        {!touchpoint.enabled && <SectionDescription>{texts.touchpointDisabled}</SectionDescription>}
      </Row>
      {!!touchpoint.enabled && (
        <>
          <StyledTextField
            label={isMessageOptional ? texts.optionalMessageLabel : texts.messageLabel}
            placeholder={isMessageOptional ? texts.optionalMessagePlaceholder : texts.messagePlaceholder}
            value={message.text || ''}
            onChange={(event) => onChange({ message: { ...message, text: event.target.value } })}
            multiline
            minRows={2}
            size="small"
          />
          <AttachmentsContainer
            fileAttachments={fileAttachments}
            onFilesSelected={handleFilesSelected}
            onFileRemoved={handleFileRemoved}
            isUploading={false}
          />
          <MediaHint>{format(texts.mediaHint, { maxFileSize: MAX_FILE_UPLOAD_SIZE_MB })}</MediaHint>
          {isMessageOptional && <MediaHint>{texts.optionalMessageHint}</MediaHint>}
          <OfferTitle>{texts.offerTitle}</OfferTitle>
          <OfferForm
            offer={touchpoint.offer}
            onChange={(offer) => onChange({ offer })}
            products={products}
            currency={currency}
            programs={programs}
            mealPlans={mealPlans}
          />
        </>
      )}
    </TouchpointContainer>
  );
};

TouchpointEditor.propTypes = {
  title: PropTypes.string.isRequired,
  touchpoint: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  currency: PropTypes.string.isRequired,
  programs: PropTypes.array.isRequired,
  mealPlans: PropTypes.array.isRequired,
  isMessageOptional: PropTypes.bool,
};

TouchpointEditor.defaultProps = {
  isMessageOptional: false,
};

export default TouchpointEditor;
